import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSaaSAuth } from '../contexts/SaaSAuthContext';
import { useTheme } from '../contexts/ThemeContext';
import LanguageSelector from './LanguageSelector';
import {
  Buildings, Users, MagnifyingGlass, Export, Gear, ListChecks,
  ChartBar, AddressBook, PlugsConnected, ClockCounterClockwise,
  Heartbeat, SignOut, List, X, Compass, Question, Sun, Moon, Lightning,
  CreditCard, UserCircleCheck,
} from '@phosphor-icons/react';

const NAV_ITEMS = [
  { to: '/app', icon: ChartBar, labelKey: 'nav.dashboard', testId: 'dashboard', end: true },
  { to: '/app/companies', icon: Buildings, labelKey: 'nav.companies', testId: 'companies' },
  { to: '/app/contacts', icon: AddressBook, labelKey: 'nav.contacts', testId: 'contacts' },
  { to: '/app/review-contacts', icon: UserCircleCheck, labelKey: 'nav.reviewContacts', testId: 'review-contacts' },
  { to: '/app/review', icon: ListChecks, labelKey: 'nav.review', testId: 'review-queue' },
  { to: '/app/discovery', icon: Compass, labelKey: 'nav.discovery', testId: 'discovery' },
  { to: '/app/imports', icon: MagnifyingGlass, labelKey: 'nav.imports', testId: 'imports' },
  { to: '/app/exports', icon: Export, labelKey: 'nav.exports', testId: 'exports' },
  { divider: true },
  { to: '/app/scoring', icon: ChartBar, labelKey: 'nav.scoring', testId: 'scoring-rules' },
  { to: '/app/integrations', icon: PlugsConnected, labelKey: 'nav.integrations', testId: 'integrations' },
  { to: '/app/billing', icon: CreditCard, labelKey: 'nav.billing', testId: 'billing' },
  { to: '/app/settings', icon: Gear, labelKey: 'nav.settings', testId: 'settings' },
  { to: '/app/users', icon: Users, labelKey: 'nav.team', testId: 'team', adminOnly: true },
  { to: '/app/audit-logs', icon: ClockCounterClockwise, labelKey: 'nav.audit', testId: 'audit-logs', adminOnly: true },
  { to: '/app/subscriptions', icon: CreditCard, labelKey: 'nav.subscriptions', testId: 'customer-access', superAdminOnly: true },
  { to: '/app/products', icon: CreditCard, labelKey: 'nav.products', testId: 'products-pricing', superAdminOnly: true },
  { to: '/app/system-health', icon: Heartbeat, labelKey: 'nav.system', testId: 'system-health', superAdminOnly: true },
  { to: '/app/help', icon: Question, labelKey: 'nav.help', testId: 'help' },
];

export default function Layout() {
  const { user, logout } = useSaaSAuth();
  const { t, i18n } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const role = user?.role || '';
  const isSuperAdmin = role === 'super_admin';
  const isAdminish = isSuperAdmin || role === 'account_owner' || role === 'admin';
  const entitlements = user?.entitlements || {};
  const subscription = entitlements.subscription || {};
  const isReadOnly = Boolean(user?.read_only || entitlements.read_only);
  const trialDate = subscription.trial_ends_at || user?.trial_ends_at;
  const dateLocale = i18n.resolvedLanguage === 'de' ? 'de-DE' : i18n.resolvedLanguage === 'es' ? 'es-ES' : 'en-GB';

  const handleLogout = async () => {
    await logout();
    navigate('/signin');
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background text-foreground" data-testid="app-layout">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/20 dark:bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50 w-64
        bg-gray-50 dark:bg-[#111113] border-r border-border
        transform transition-transform lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        flex flex-col
      `}>
        <div className="h-16 flex items-center px-5 border-b border-border shrink-0">
          <Lightning size={24} weight="fill" className="text-foreground mr-2.5 shrink-0" />
          <div className="min-w-0">
            <span className="font-heading text-base font-bold tracking-tight text-foreground block leading-tight">Mayr Lead Creator</span>
            <span className="text-[10px] font-mono text-muted-foreground tracking-wider">
              {isSuperAdmin ? 'SUPER ADMIN' : (user?.account_name || 'MAYR-TECHNOLOGIES').toUpperCase()}
            </span>
          </div>
          <button className="ml-auto lg:hidden text-muted-foreground" onClick={() => setSidebarOpen(false)}>
            <X size={20} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-3">
          {NAV_ITEMS.map((item, i) => {
            if (item.divider) return <div key={i} className="my-3 border-t border-border" />;
            if (item.superAdminOnly && !isSuperAdmin) return null;
            if (item.adminOnly && !isAdminish) return null;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors mb-0.5
                  ${isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'}`
                }
                data-testid={`nav-${item.testId}`}
              >
                <item.icon size={18} weight={item.end ? 'bold' : 'regular'} />
                {t(item.labelKey)}
              </NavLink>
            );
          })}
        </nav>

        <div className="border-t border-border p-4 shrink-0">
          <LanguageSelector className="mb-3" />
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-xs font-bold shrink-0">
              {(user?.name || user?.email || '?')[0].toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate">{user?.name || t('common.user')}</p>
              <p className="text-xs text-muted-foreground truncate">{role.replace(/_/g, ' ')}</p>
            </div>
            <button onClick={toggleTheme} className="text-muted-foreground hover:text-foreground transition-colors" data-testid="theme-toggle-btn" title={theme === 'dark' ? t('common.lightMode') : t('common.darkMode')}>
              {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <button onClick={handleLogout} className="text-muted-foreground hover:text-foreground transition-colors" data-testid="logout-btn" title={t('common.logout')}>
              <SignOut size={18} />
            </button>
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b border-border flex items-center px-6 shrink-0 bg-background">
          <button className="lg:hidden mr-4 text-foreground" onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-btn">
            <List size={24} />
          </button>
          <div className="flex-1" />
          {user?.plan && (
            <span
              className={`hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 mr-4 rounded-full bg-accent text-xs font-mono uppercase tracking-wider text-accent-foreground ${isReadOnly ? 'opacity-70' : ''}`}
              data-testid="plan-badge"
            >
              <span className={`w-1.5 h-1.5 rounded-full ${isReadOnly ? 'bg-amber-500' : 'bg-emerald-500'}`} />
              {user.plan}{subscription.status === 'trialing' || user.subscription_status === 'trialing' ? ` · ${t('access.trial')}` : ''}
              {isReadOnly ? ` · ${t('admin.readOnly')}` : ''}
            </span>
          )}
          <span className="text-xs font-mono text-muted-foreground uppercase tracking-wider">
            {user?.email}
          </span>
        </header>
        {isReadOnly ? (
          <div className="border-b border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100" data-testid="read-only-banner">
            <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3">
              <div><strong>{t('access.readOnlyTitle')}</strong> <span className="ml-1">{t('access.readOnlyBody')}</span></div>
              <button onClick={() => navigate('/app/billing')} className="rounded bg-amber-950 px-3 py-1.5 text-xs font-semibold text-white dark:bg-amber-200 dark:text-amber-950">{t('access.choosePlan')}</button>
            </div>
          </div>
        ) : trialDate && (subscription.status === 'trialing' || user?.subscription_status === 'trialing') ? (
          <div className="border-b border-blue-200 bg-blue-50 px-4 py-2 text-xs text-blue-950 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-100" data-testid="trial-banner">
            <div className="mx-auto flex max-w-7xl items-center justify-between gap-3">
              <span>{t('access.trialBanner', { date: new Date(trialDate).toLocaleDateString(dateLocale) })}</span>
              <button onClick={() => navigate('/app/billing')} className="font-semibold underline">{t('access.choosePlan')}</button>
            </div>
          </div>
        ) : null}
        <main className="flex-1 overflow-y-auto overflow-x-hidden p-6 bg-muted/30 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
